// SelectionMarchingAnts — extracts the boundary of a Selection at the 50%
// mask threshold as a set of polylines. A renderer can later stroke these
// with the classic dashed "marching ants" pattern.
//
// Algorithm:
//   1. Binarize: a pixel (x,y) is "inside" iff mask[y*w+x] >= 128. Pixels
//      outside the document also count as outside.
//   2. For every horizontal edge between (x,y-1) and (x,y) where exactly one
//      side is inside, emit a unit segment from (x, y) to (x+1, y).
//      Likewise for vertical edges between (x-1,y) and (x,y): segment from
//      (x, y) to (x, y+1).
//   3. Chain segments into polylines by walking from each endpoint to the
//      next unused segment that shares the endpoint. Each polyline is closed
//      when it returns to its start (we still emit it as a polyline with the
//      start point repeated at the end).
//
// Coordinates are pixel-grid corners (integers in [0, docW] x [0, docH]).
//
// Layer 3 (Document Model). No DOM.

import type { Selection } from "./Selection";

export interface Point {
  x: number;
  y: number;
}

const THRESHOLD = 128;

interface Segment {
  // a -> b, both integer pixel-corner coords. Each segment is unit length and
  // axis-aligned. `used` flag prevents re-walking.
  ax: number;
  ay: number;
  bx: number;
  by: number;
  used: boolean;
}

/**
 * Returns boundary polylines for a Selection at the 50% threshold.
 * Empty selections return an empty array.
 */
export function extractBoundary(sel: Selection): Point[][] {
  if (sel.isEmpty()) return [];
  const w = sel.docW;
  const h = sel.docH;
  const mask = sel.mask;

  // Walk only within the inflated bounds (1px of slack so we catch edges that
  // touch the document border).
  const b = sel.bounds();
  if (b === null) return [];
  const x0 = Math.max(0, b.x - 0);
  const y0 = Math.max(0, b.y - 0);
  const x1 = Math.min(w, b.x + b.w);
  const y1 = Math.min(h, b.y + b.h);

  const inside = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= w || y >= h) return false;
    return (mask[y * w + x] ?? 0) >= THRESHOLD;
  };

  // Collect edge segments. We index by their starting corner for fast chain
  // lookup later.
  const byStart = new Map<number, Segment[]>();
  const segs: Segment[] = [];
  const corner = (cx: number, cy: number): number => cy * (w + 1) + cx;
  const pushSeg = (ax: number, ay: number, bx: number, by: number): void => {
    const s: Segment = { ax, ay, bx, by, used: false };
    segs.push(s);
    const k = corner(ax, ay);
    const arr = byStart.get(k);
    if (arr) arr.push(s);
    else byStart.set(k, [s]);
  };

  // Horizontal boundary edges: between row y-1 (above) and row y (below).
  // We need y in [y0, y1] inclusive, plus the row just above/below the bbox
  // so we capture edges that touch the bbox border.
  const hy0 = Math.max(0, y0);
  const hy1 = Math.min(h, y1);
  for (let y = hy0; y <= hy1; y++) {
    for (let x = x0; x < x1; x++) {
      const above = inside(x, y - 1);
      const below = inside(x, y);
      if (above === below) continue;
      // Orient so that "inside" is on the left of the walking direction:
      // if below is inside, we walk left-to-right (a -> b along +x).
      // if above is inside, we walk right-to-left (a -> b along -x).
      if (below) pushSeg(x, y, x + 1, y);
      else pushSeg(x + 1, y, x, y);
    }
  }
  // Vertical boundary edges: between column x-1 (left) and column x (right).
  const vx0 = Math.max(0, x0);
  const vx1 = Math.min(w, x1);
  for (let x = vx0; x <= vx1; x++) {
    for (let y = y0; y < y1; y++) {
      const left = inside(x - 1, y);
      const right = inside(x, y);
      if (left === right) continue;
      // If right is inside, walk top-to-bottom (a -> b along +y) so inside
      // stays on the left.
      if (left) pushSeg(x, y, x, y + 1);
      else pushSeg(x, y + 1, x, y);
    }
  }

  // Chain segments into polylines.
  const polylines: Point[][] = [];
  for (const start of segs) {
    if (start.used) continue;
    start.used = true;
    const poly: Point[] = [
      { x: start.ax, y: start.ay },
      { x: start.bx, y: start.by },
    ];
    let cx = start.bx;
    let cy = start.by;
    // Follow the chain. There may be more than one outgoing segment from a
    // corner (where the boundary pinches); pick the first unused one. This
    // matches a typical "march around the region" walk.
    // We also bail on cycles back to start.
    // Maximum steps guard prevents pathological loops.
    const maxSteps = segs.length + 1;
    for (let step = 0; step < maxSteps; step++) {
      const outs = byStart.get(corner(cx, cy));
      if (!outs) break;
      let next: Segment | null = null;
      for (const s of outs) {
        if (!s.used) {
          next = s;
          break;
        }
      }
      if (!next) break;
      next.used = true;
      cx = next.bx;
      cy = next.by;
      poly.push({ x: cx, y: cy });
      if (cx === start.ax && cy === start.ay) break;
    }
    polylines.push(poly);
  }

  return polylines;
}
